/**
 * Caché del espacio de trabajo activo, alimentada por Supabase.
 *
 * Los componentes siguen leyendo de forma síncrona a través de
 * `useDatabase()` / `useWorkspace()`. Los servicios escriben en Supabase y
 * después llaman a `refresh()` para releer los datos con RLS aplicado.
 * Solo los archivos de /services deben tocar este módulo.
 */

import {
  Form,
  FormAnswer,
  FormSubmission,
  Invitation,
  LandingPage,
  Lead,
  LeadNote,
  LeadSource,
  LeadStatusHistory,
  LeadFile,
  Organization,
  OrganizationMember,
  PipelineStage,
  SavedComponent,
  User,
} from '../types'
import { supabase, BUCKETS } from './supabaseClient'
import {
  Row,
  toBlock,
  toForm,
  toFile,
  toHistory,
  toInvitation,
  toLanding,
  toLead,
  toMember,
  toNote,
  toOrganization,
  toQuestion,
  toSavedComponent,
  toSource,
  toStage,
} from './mappers'

export interface Database {
  users: User[]
  organizations: Organization[]
  members: OrganizationMember[]
  invitations: Invitation[]
  stages: PipelineStage[]
  sources: LeadSource[]
  forms: Form[]
  landings: LandingPage[]
  leads: Lead[]
  notes: LeadNote[]
  history: LeadStatusHistory[]
  submissions: FormSubmission[]
  components: SavedComponent[]
  files: LeadFile[]
  /** Siempre false: los datos provienen de Supabase. */
  demo: boolean
}

function empty(): Database {
  return {
    users: [],
    organizations: [],
    members: [],
    invitations: [],
    stages: [],
    sources: [],
    forms: [],
    landings: [],
    leads: [],
    notes: [],
    history: [],
    submissions: [],
    components: [],
    files: [],
    demo: false,
  }
}

let db: Database = empty()
let currentOrganizationId = ''
const listeners = new Set<() => void>()

export function getDb(): Database {
  return db
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function mutate(updater: (current: Database) => Database): void {
  db = updater(db)
  listeners.forEach((listener) => listener())
}

/** Clave usada en `organization_settings` para la configuración extra. */
export function configKey(kind: 'form' | 'landing', id: string): string {
  return `${kind}_config:${id}`
}

/** Guarda un bloque de configuración que no tiene columna propia. */
export async function saveConfig(
  organizationId: string,
  kind: 'form' | 'landing',
  id: string,
  value: Row,
): Promise<void> {
  await supabase
    .from('organization_settings')
    .upsert(
      { organization_id: organizationId, key: configKey(kind, id), value },
      { onConflict: 'organization_id,key' },
    )
}

async function fetchSettings(organizationId: string): Promise<Map<string, Row>> {
  const { data } = await supabase
    .from('organization_settings')
    .select('key, value')
    .eq('organization_id', organizationId)
  const map = new Map<string, Row>()
  ;(data ?? []).forEach((row: Row) => map.set(row.key, row.value ?? {}))
  return map
}

/** Carga TODO el espacio de trabajo de una organización desde Supabase. */
export async function hydrate(organizationId: string): Promise<void> {
  if (!organizationId) {
    db = empty()
    listeners.forEach((l) => l())
    return
  }
  currentOrganizationId = organizationId

  const [
    orgRes,
    membersRes,
    invitationsRes,
    stagesRes,
    sourcesRes,
    formsRes,
    questionsRes,
    landingsRes,
    blocksRes,
    leadsRes,
    notesRes,
    activitiesRes,
    submissionsRes,
    answersRes,
    componentsRes,
    settings,
  ] = await Promise.all([
    supabase.from('organizations').select('*').eq('id', organizationId).maybeSingle(),
    supabase
      .from('organization_members')
      .select('*, profiles(*)')
      .eq('organization_id', organizationId),
    supabase
      .from('organization_invitations')
      .select('*')
      .eq('organization_id', organizationId),
    supabase
      .from('pipeline_stages')
      .select('*')
      .eq('organization_id', organizationId)
      .is('deleted_at', null)
      .order('position'),
    supabase
      .from('lead_sources')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name'),
    supabase.from('forms').select('*').eq('organization_id', organizationId),
    supabase
      .from('form_questions')
      .select('*')
      .eq('organization_id', organizationId)
      .order('position'),
    supabase.from('landing_pages').select('*').eq('organization_id', organizationId),
    supabase
      .from('landing_blocks')
      .select('*')
      .eq('organization_id', organizationId)
      .order('position'),
    supabase
      .from('leads')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false }),
    supabase
      .from('lead_notes')
      .select('*')
      .eq('organization_id', organizationId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false }),
    supabase
      .from('lead_activities')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false }),
    supabase
      .from('form_submissions')
      .select('*')
      .eq('organization_id', organizationId),
    supabase.from('form_answers').select('*').eq('organization_id', organizationId),
    supabase
      .from('component_library')
      .select('*')
      .eq('organization_id', organizationId),
    fetchSettings(organizationId),
  ])

  const members = (membersRes.data ?? []).map(toMember)
  const nameOf = (userId: string) => {
    const member = members.find((m) => m.userId === userId)
    return member ? `${member.firstName} ${member.lastName}`.trim() : 'Sistema'
  }

  const sources = (sourcesRes.data ?? []).map((row: Row, i: number) =>
    toSource(row, i),
  )
  const sourceName = (id?: string) =>
    id ? (sources.find((s) => s.id === id)?.key ?? '') : ''

  const questions = (questionsRes.data ?? []).map(toQuestion)
  const forms = (formsRes.data ?? []).map((row: Row) =>
    toForm(
      row,
      settings.get(configKey('form', row.id)) ?? {},
      questions.filter((q) => q.formId === row.id),
    ),
  )

  const blocks = (blocksRes.data ?? []) as Row[]
  const landings = (landingsRes.data ?? []).map((row: Row) =>
    toLanding(
      row,
      settings.get(configKey('landing', row.id)) ?? {},
      blocks.filter((b) => b.landing_id === row.id).map(toBlock),
    ),
  )

  // Respuestas del formulario asociadas a cada lead.
  const questionLabel = (id: string) =>
    questions.find((q) => q.id === id)?.label ?? ''
  const answersBySubmission = new Map<string, FormAnswer[]>()
  ;(answersRes.data ?? []).forEach((row: Row) => {
    const list = answersBySubmission.get(row.submission_id) ?? []
    list.push({
      questionId: row.question_id,
      label: questionLabel(row.question_id),
      value: row.value,
    })
    answersBySubmission.set(row.submission_id, list)
  })
  const answersByLead = new Map<string, FormAnswer[]>()
  const submissions: FormSubmission[] = (submissionsRes.data ?? []).map(
    (row: Row) => {
      const answers = answersBySubmission.get(row.id) ?? []
      if (row.lead_id) answersByLead.set(row.lead_id, answers)
      return {
        id: row.id,
        organizationId: row.organization_id,
        formId: row.form_id,
        landingId: row.landing_id ?? undefined,
        leadId: row.lead_id,
        answers,
        createdAt: row.created_at,
      }
    },
  )

  const organization = orgRes.data ? toOrganization(orgRes.data) : null

  db = {
    users: members.map((m) => ({
      id: m.userId,
      firstName: m.firstName,
      lastName: m.lastName,
      email: m.email,
      avatarUrl: m.avatarUrl,
      createdAt: m.createdAt,
    })),
    organizations: organization ? [organization] : [],
    members,
    invitations: (invitationsRes.data ?? []).map(toInvitation),
    stages: (stagesRes.data ?? []).map(toStage),
    sources,
    forms,
    landings,
    leads: (leadsRes.data ?? []).map((row: Row) =>
      toLead(row, sourceName, answersByLead.get(row.id) ?? []),
    ),
    notes: (notesRes.data ?? []).map((row: Row) => toNote(row, nameOf)),
    history: (activitiesRes.data ?? []).map((row: Row) => toHistory(row, nameOf)),
    submissions,
    components: (componentsRes.data ?? []).map(toSavedComponent),
    files: [],
    demo: false,
  }

  listeners.forEach((l) => l())
  void hydrateFiles(organizationId)
}

/** Los archivos viven en un bucket privado: se firman aparte. */
async function hydrateFiles(organizationId: string): Promise<void> {
  const { data } = await supabase
    .from('lead_activities')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('type', 'archivo')
  if (!data?.length) return
  const files = await Promise.all(
    data.map(async (row: Row) => {
      const meta = (row.metadata ?? {}) as Row
      const { data: signed } = await supabase.storage
        .from(BUCKETS.leadFiles)
        .createSignedUrl(meta.path ?? '', 3600)
      return toFile(
        {
          id: row.id,
          organization_id: row.organization_id,
          lead_id: row.lead_id,
          name: meta.name,
          size: meta.size,
          type: meta.type,
          uploaded_by: meta.uploadedBy,
          created_at: row.created_at,
        },
        signed?.signedUrl ?? '',
      )
    }),
  )
  mutate((current) => ({ ...current, files }))
}

/** Recarga el espacio de trabajo activo tras cualquier escritura. */
export async function refresh(): Promise<void> {
  if (currentOrganizationId) await hydrate(currentOrganizationId)
}

export function activeOrganizationId(): string {
  return currentOrganizationId
}

/** Limpia la caché al cerrar sesión. */
export function resetStore(): void {
  currentOrganizationId = ''
  db = empty()
  listeners.forEach((l) => l())
}

/** Inserta en la caché una landing o formulario público (visitante anónimo). */
export function mergePublic(patch: Partial<Database>): void {
  mutate((current) => ({
    ...current,
    forms: patch.forms
      ? [
          ...current.forms.filter(
            (f) => !patch.forms?.some((n) => n.id === f.id),
          ),
          ...patch.forms,
        ]
      : current.forms,
    landings: patch.landings
      ? [
          ...current.landings.filter(
            (l) => !patch.landings?.some((n) => n.id === l.id),
          ),
          ...patch.landings,
        ]
      : current.landings,
    organizations: patch.organizations
      ? [
          ...current.organizations.filter(
            (o) => !patch.organizations?.some((n) => n.id === o.id),
          ),
          ...patch.organizations,
        ]
      : current.organizations,
  }))
}

/** Compatibilidad: ya no existen datos de demostración. */
export function clearDemoData(): void {
  mutate((current) => ({ ...current, demo: false }))
}

/** Utilidad heredada para mantener las firmas de los servicios. */
export function delay<T>(value: T, ms = 0): Promise<T> {
  return ms > 0
    ? new Promise((resolve) => setTimeout(() => resolve(value), ms))
    : Promise.resolve(value)
}
